import { callAI } from '@4cm/core/ai';
import { createMemory, searchMemories } from '@4cm/brain/brainMemory';
import type { RadarValues } from './queries/useTeamRadar';

/**
 * Parent digest (Layer 3, scope='player', tag='parent-digest').
 *
 * Coach taps Draft on a player. Brain composes a 2-3 sentence
 * fortnightly summary suitable for a parent. Coach reviews + edits +
 * saves. The saved digest becomes a brain_memory so future Brain turns
 * see the parent-facing narrative as a prior. Delivery to parents
 * (email/push) is a separate phase.
 */

const SYSTEM_PROMPT = `You write short, warm, specific parent updates for a youth football coach.

Audience: a parent or carer of a 7–18 year old player.
Tone: warm, specific, concrete. No coach-speak, no 4-corner jargon, no scores. No bullet points.
Length: 2 to 3 sentences. ~50 words. Past two weeks.
Forbidden: "your child", "kiddo", "champ", "great job!", emojis, exclamation marks, "as parents".
Required: at least one specific moment or trait (use the goals + recent context). End with one concrete thing the parent can do or notice this fortnight.

Return plain text. No greeting, no signoff, no quotation marks.`;

interface DraftOptions {
  playerName: string;
  position?: string | null;
  ratings?: RadarValues;
  ratingsCount?: number;
  goals?: string[];
  recentSessionTitles?: string[];
  coachLevel?: string | null;
}

export async function draftParentDigest(opts: DraftOptions): Promise<string | null> {
  const userContent = JSON.stringify({
    player_name: opts.playerName,
    position: opts.position ?? null,
    ratings_28d: opts.ratings ?? null,
    ratings_count: opts.ratingsCount ?? 0,
    active_goals: opts.goals ?? [],
    recent_sessions: opts.recentSessionTitles ?? [],
    coach_level: opts.coachLevel ?? null,
  });

  const text = await callAI({
    system: SYSTEM_PROMPT,
    userContent,
    maxTokens: 220,
    surface: 'brain-chat',
    effort: 'low',
  });
  return text?.trim() ?? null;
}

interface SaveOptions {
  coachId: string;
  teamId: string | null;
  playerId: string;
  playerName: string;
  text: string;
}

export async function saveParentDigest(
  opts: SaveOptions,
): Promise<{ ok: true; memoryId: string } | { ok: false; error: string }> {
  const fact = `Parent digest for ${opts.playerName} (${new Date().toLocaleDateString()}):\n${opts.text}`;
  const memory = await createMemory({
    coach_id: opts.coachId,
    team_id: opts.teamId,
    scope: 'player',
    entity_id: opts.playerId,
    kind: 'reflection',
    fact,
    tags: ['parent-digest', 'layer3', 'fortnightly'],
    confidence: 0.9,
  });
  if (!memory) return { ok: false, error: 'Could not save digest.' };
  return { ok: true, memoryId: memory.id };
}

export interface ParentDigestSummary {
  id: string;
  fact: string;
  created_at: string;
}

export async function listPlayerParentDigests(
  coachId: string,
  playerId: string,
  limit = 5,
): Promise<ParentDigestSummary[]> {
  const memories = await searchMemories({
    coach_id: coachId,
    scope: 'player',
    entity_id: playerId,
    tags: ['parent-digest'],
    limit,
  });
  return memories.map((m) => ({
    id: m.id,
    fact: m.fact,
    created_at: m.created_at,
  }));
}
